export interface AgentTemplate {
  id: string;
  name: string;
  description: string;
  category: string;
  agents: AgentTemplateAgent[];
  connections: AgentTemplateConnection[];
}

export interface AgentTemplateAgent {
  id: string;
  name: string;
  role: string;
  type: 'coordinator' | 'worker' | 'reviewer' | 'specialist';
  color: string;
  x?: number;
  y?: number;
}

export interface AgentTemplateConnection {
  from: string;
  to: string;
  label: string;
}

export const templates: AgentTemplate[] = [
  {
    id: 'customer-support',
    name: 'Customer Support Swarm',
    description: 'Automated customer support with escalation handling and sentiment analysis.',
    category: 'Support',
    agents: [
      { id: 'ingress', name: 'Ticket Router', role: 'Routes incoming tickets to the right handler', type: 'coordinator', color: '#8b5cf6', x: 300, y: 80 },
      { id: 'sentiment', name: 'Sentiment Analyzer', role: 'Analyzes customer emotion and urgency', type: 'specialist', color: '#ec4899', x: 100, y: 200 },
      { id: 'responder', name: 'Auto Responder', role: 'Drafts contextual responses', type: 'worker', color: '#3b82f6', x: 300, y: 200 },
      { id: 'escalation', name: 'Escalation Handler', role: 'Escalates complex issues to humans', type: 'worker', color: '#f59e0b', x: 500, y: 200 },
    ],
    connections: [
      { from: 'ingress', to: 'sentiment', label: 'analyze' },
      { from: 'ingress', to: 'responder', label: 'route' },
      { from: 'sentiment', to: 'responder', label: 'context' },
      { from: 'responder', to: 'escalation', label: 'escalate' },
    ],
  },
  {
    id: 'research-team',
    name: 'Research Team Swarm',
    description: 'Collaborative research with source gathering, fact-checking, and report generation.',
    category: 'Research',
    agents: [
      { id: 'lead', name: 'Research Lead', role: 'Coordinates research tasks and priorities', type: 'coordinator', color: '#8b5cf6', x: 300, y: 80 },
      { id: 'gatherer', name: 'Source Gatherer', role: 'Finds and collects relevant sources', type: 'worker', color: '#10b981', x: 120, y: 200 },
      { id: 'analyzer', name: 'Content Analyzer', role: 'Extracts key findings from sources', type: 'specialist', color: '#3b82f6', x: 300, y: 200 },
      { id: 'factchecker', name: 'Fact Checker', role: 'Verifies claims across multiple sources', type: 'reviewer', color: '#f59e0b', x: 480, y: 200 },
      { id: 'writer', name: 'Report Writer', role: 'Compiles findings into structured reports', type: 'worker', color: '#ec4899', x: 300, y: 320 },
    ],
    connections: [
      { from: 'lead', to: 'gatherer', label: 'assign' },
      { from: 'lead', to: 'analyzer', label: 'assign' },
      { from: 'gatherer', to: 'analyzer', label: 'sources' },
      { from: 'analyzer', to: 'factchecker', label: 'verify' },
      { from: 'factchecker', to: 'writer', label: 'validated' },
      { from: 'writer', to: 'lead', label: 'draft' },
    ],
  },
  {
    id: 'sales-outreach',
    name: 'Sales Outreach Swarm',
    description: 'Automated sales pipeline with lead scoring, outreach, and follow-up.',
    category: 'Sales',
    agents: [
      { id: 'prospector', name: 'Lead Prospector', role: 'Identifies and qualifies potential leads', type: 'worker', color: '#10b981', x: 300, y: 80 },
      { id: 'scorer', name: 'Lead Scorer', role: 'Scores leads based on fit and intent', type: 'specialist', color: '#f59e0b', x: 120, y: 200 },
      { id: 'outreach', name: 'Outreach Crafter', role: 'Personalizes messages for each lead', type: 'worker', color: '#3b82f6', x: 300, y: 200 },
      { id: 'scheduler', name: 'Meeting Scheduler', role: 'Books meetings with qualified leads', type: 'worker', color: '#8b5cf6', x: 480, y: 200 },
      { id: 'closer', name: 'Deal Closer', role: 'Handles final negotiations and closes', type: 'coordinator', color: '#ec4899', x: 300, y: 320 },
    ],
    connections: [
      { from: 'prospector', to: 'scorer', label: 'qualify' },
      { from: 'scorer', to: 'outreach', label: 'scored' },
      { from: 'outreach', to: 'scheduler', label: 'engage' },
      { from: 'scheduler', to: 'closer', label: 'booked' },
    ],
  },
  {
    id: 'content-creation',
    name: 'Content Creation Swarm',
    description: 'End-to-end content pipeline from ideation to publication.',
    category: 'Content',
    agents: [
      { id: 'ideator', name: 'Topic Ideator', role: 'Researches trending topics and generates ideas', type: 'worker', color: '#f59e0b', x: 300, y: 80 },
      { id: 'researcher', name: 'Content Researcher', role: 'Gathers data and references for articles', type: 'worker', color: '#10b981', x: 120, y: 200 },
      { id: 'writer', name: 'Content Writer', role: 'Drafts articles and blog posts', type: 'worker', color: '#3b82f6', x: 300, y: 200 },
      { id: 'editor', name: 'Editor', role: 'Reviews and polishes content', type: 'reviewer', color: '#8b5cf6', x: 480, y: 200 },
      { id: 'seo', name: 'SEO Optimizer', role: 'Optimizes content for search engines', type: 'specialist', color: '#ec4899', x: 300, y: 320 },
    ],
    connections: [
      { from: 'ideator', to: 'researcher', label: 'topic' },
      { from: 'researcher', to: 'writer', label: 'materials' },
      { from: 'writer', to: 'editor', label: 'draft' },
      { from: 'editor', to: 'seo', label: 'polished' },
    ],
  },
  {
    id: 'data-analysis',
    name: 'Data Analysis Swarm',
    description: 'Automated data pipeline with collection, cleaning, analysis, and visualization.',
    category: 'Analytics',
    agents: [
      { id: 'collector', name: 'Data Collector', role: 'Gathers data from multiple sources', type: 'worker', color: '#3b82f6', x: 300, y: 80 },
      { id: 'cleaner', name: 'Data Cleaner', role: 'Validates and cleans raw data', type: 'specialist', color: '#f59e0b', x: 120, y: 200 },
      { id: 'analyst', name: 'Statistical Analyst', role: 'Performs statistical analysis and modeling', type: 'worker', color: '#10b981', x: 300, y: 200 },
      { id: 'visualizer', name: 'Visualization Agent', role: 'Creates charts and dashboards', type: 'worker', color: '#8b5cf6', x: 480, y: 200 },
      { id: 'reporter', name: 'Insight Reporter', role: 'Summarizes findings and recommendations', type: 'coordinator', color: '#ec4899', x: 300, y: 320 },
    ],
    connections: [
      { from: 'collector', to: 'cleaner', label: 'raw data' },
      { from: 'cleaner', to: 'analyst', label: 'clean data' },
      { from: 'analyst', to: 'visualizer', label: 'analysis' },
      { from: 'visualizer', to: 'reporter', label: 'charts' },
    ],
  },
];
